import React, { useContext, useEffect } from "react";
import { secured } from "react-abac";
import { AnimClassEnum, AnimOrderEnum, AuthoritiesEnum, CRUDActionEnum, RolesSortByEnum, StatusStateEnum } from "~/types/enums";
import RestrictedSection from "~/components/RestrictedSection";
import { Button, Dropdown, Grid, Icon, InputText, Modal, Section, Spinner, Table, Text } from "~/components-shared";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import CoreContext from "~/lib/context";
import manageRoleSelectors from "~/store/manageRoles/selectors";
import useAbacExt from "~/hooks/abac";
import manageRoleActionCreator from "~/store/manageRoles/actions";
import styled, { useTheme } from "styled-components";
import RoleUpdateModal from "~/components/RoleUpdateModal";
import RoleDeleteModal from "~/components/RoleDeleteModal";
import RoleCreateModal from "~/components/RoleCreateModal";
import moment from "moment/moment";
const StyledTable = styled(Table)`
  width: calc(100% + 112px);
`;
const RoleManagement = () => {
  const {
    colors
  } = useTheme();
  const {
    t
  } = useTranslation();
  const dispatch = useDispatch();
  const {
    setSidebar
  } = useContext(CoreContext);
  const roles = useSelector(manageRoleSelectors.getRoles);
  const action = useSelector(manageRoleSelectors.getAction);
  const status = useSelector(manageRoleSelectors.getStatus);
  const sort = useSelector(manageRoleSelectors.getSort);
  const filter = useSelector(manageRoleSelectors.getFilter);
  const {
    hasAfter
  } = useSelector(manageRoleSelectors.getPagination);
  const {
    userHas,
    userHasOneOf
  } = useAbacExt();
  const columnCount = userHasOneOf([AuthoritiesEnum.MANAGE_ROLES_WRITE]) ? 8 : 7;
  useEffect(() => {
    if (status === StatusStateEnum.IDLE) {
      dispatch(manageRoleActionCreator.fetchStarted());
    }
  }, [dispatch, status]);
  useEffect(() => {
    setSidebar(undefined);
  }, [setSidebar]);
  const handleSort = newSort => {
    dispatch(manageRoleActionCreator.sorted(newSort));
  };
  const handleCreate = () => {
    dispatch(manageRoleActionCreator.actionChanged(CRUDActionEnum.CREATE, undefined));
  };
  const handleUpdate = roleId => {
    dispatch(manageRoleActionCreator.actionChanged(CRUDActionEnum.UPDATE, roleId));
  };
  const handleDelete = roleId => {
    dispatch(manageRoleActionCreator.actionChanged(CRUDActionEnum.DELETE, roleId));
  };
  const handleFilter = newFilter => {
    dispatch(manageRoleActionCreator.filtered(newFilter));
  };
  const handleLoadMore = () => {
    dispatch(manageRoleActionCreator.loadedMore());
  };
  return /*#__PURE__*/React.createElement(Section, null, /*#__PURE__*/React.createElement(Text, {
    element: "h1",
    s: {
      mb: 10
    },
    className: `${AnimOrderEnum.ONE} ${AnimClassEnum.POP_RIGHT_FADE}`
  }, t("RoleManagement.roleManagement")), /*#__PURE__*/React.createElement(Grid, {
    cols: 3,
    gap: 3,
    s: {
      mb: 8
    },
    template: userHas(AuthoritiesEnum.MANAGE_ROLES_WRITE) ? "1fr 3.1rem" : "1fr"
  }, /*#__PURE__*/React.createElement(InputText, {
    value: filter?.query || "",
    placeholder: t("RoleManagement.search"),
    elemBefore: /*#__PURE__*/React.createElement(Icon, {
      color: colors.text.secondary,
      src: "search",
      s: {
        mr: 2
      }
    }),
    color: "translucent",
    onChange: value => handleFilter({
      ...filter,
      query: value
    }),
    clearButton: true
  }), userHas(AuthoritiesEnum.MANAGE_ROLES_WRITE) && /*#__PURE__*/React.createElement(Button, {
    "data-tooltip-id": "general-tooltip",
    "data-tooltip-content": t("RoleManagement.createNewRole"),
    s: {
      p: 2.8
    },
    onClick: handleCreate,
    color: "translucent"
  }, /*#__PURE__*/React.createElement(Icon, {
    src: "plus",
    scale: 5
  }))), /*#__PURE__*/React.createElement(StyledTable, {
    tableProps: {
      style: {
        minWidth: "650px"
      }
    },
    s: {
      mx: -14
    }
  }, /*#__PURE__*/React.createElement(Table.Header, null, /*#__PURE__*/React.createElement(Table.Tr, null, /*#__PURE__*/React.createElement(Table.Th, {
    width: "3.57143rem"
  }), /*#__PURE__*/React.createElement(Table.Th, {
    sort: sort,
    sortBy: [RolesSortByEnum.BY_NAME_ASC, RolesSortByEnum.BY_NAME_DESC],
    onSort: handleSort
  }, t("RoleManagement.name")), /*#__PURE__*/React.createElement(Table.Th, null, t("RoleManagement.description")), /*#__PURE__*/React.createElement(Table.Th, {
    sort: sort,
    sortBy: [RolesSortByEnum.BY_WEIGHT_ASC, RolesSortByEnum.BY_WEIGHT_DESC],
    onSort: handleSort,
    width: "6rem",
    center: true
  }, t("RoleManagement.weight")), /*#__PURE__*/React.createElement(Table.Th, {
    width: "8rem",
    center: true
  }, t("RoleManagement.authorities")), /*#__PURE__*/React.createElement(Table.Th, {
    sort: sort,
    sortBy: [RolesSortByEnum.BY_CREATION_DATE_ASC, RolesSortByEnum.BY_CREATION_DATE_DESC],
    onSort: handleSort,
    width: "12rem"
  }, t("RoleManagement.createdAt")), userHasOneOf([AuthoritiesEnum.MANAGE_ROLES_WRITE]) && /*#__PURE__*/React.createElement(Table.Th, null), /*#__PURE__*/React.createElement(Table.Th, {
    width: "3.57143rem"
  }))), /*#__PURE__*/React.createElement(Table.Body, null, !roles.length && status === StatusStateEnum.LOADING ? /*#__PURE__*/React.createElement("tr", null, /*#__PURE__*/React.createElement("td", {
    colSpan: columnCount
  }, /*#__PURE__*/React.createElement(Spinner, {
    center: true
  }))) : roles.length ? /*#__PURE__*/React.createElement(React.Fragment, null, roles.map(role => /*#__PURE__*/React.createElement(Table.Tr, {
    key: role.id
  }, /*#__PURE__*/React.createElement(Table.Td, null), /*#__PURE__*/React.createElement(Table.Td, null, /*#__PURE__*/React.createElement(Text, {
    element: "span",
    fontSize: 3.5,
    weight: "semiBold",
    display: "block"
  }, role.name)), /*#__PURE__*/React.createElement(Table.Td, null, role.description ? /*#__PURE__*/React.createElement(Text, null, role.description) : /*#__PURE__*/React.createElement(Text, {
    color: "textSecondary"
  }, "No description")), /*#__PURE__*/React.createElement(Table.Td, {
    center: true
  }, role.weight), /*#__PURE__*/React.createElement(Table.Td, {
    center: true
  }, role.authorities?.length), /*#__PURE__*/React.createElement(Table.Td, null, /*#__PURE__*/React.createElement(Text, {
    color: "textSecondary",
    display: "block",
    weight: "semiBold"
  }, moment(role.createdAt).local().startOf("seconds").fromNow(true), " ", t("Activities.ago")), /*#__PURE__*/React.createElement(Text, {
    display: "block"
  }, moment(role.createdAt).format("ddd, MMM D hh:mma"))), userHasOneOf([AuthoritiesEnum.MANAGE_ROLES_WRITE]) && /*#__PURE__*/React.createElement(Table.Td, null, /*#__PURE__*/React.createElement(Dropdown, {
    direction: "bottomLeft",
    handlerProps: {
      rounded: true,
      s: {
        p: 2
      },
      fullWidth: false,
      "data-tooltip-id": "general-tooltip",
      "data-tooltip-content": t("common.actions.options")
    },
    handler: /*#__PURE__*/React.createElement(Icon, {
      src: "dots-vertical",
      color: colors.text.primary,
      display: "block"
    })
  }, _ref => {
    let {
      isOpen,
      onItemClick
    } = _ref;
    return /*#__PURE__*/React.createElement(Dropdown.List, {
      isOpen: isOpen
    }, userHas(AuthoritiesEnum.MANAGE_ROLES_WRITE) && /*#__PURE__*/React.createElement(Dropdown.ListItem, {
      onClick: e => {
        e.stopPropagation();
        handleUpdate(role.id);
        onItemClick();
      }
    }, /*#__PURE__*/React.createElement(Icon, {
      scale: 4.5,
      src: "pencil",
      s: {
        mr: 2
      },
      color: "primary"
    }), t("common.actions.edit")), userHas(AuthoritiesEnum.MANAGE_ROLES_WRITE) && /*#__PURE__*/React.createElement(Dropdown.ListItem, {
      onClick: e => {
        e.stopPropagation();
        handleDelete(role.id);
        onItemClick();
      }
    }, /*#__PURE__*/React.createElement(Icon, {
      scale: 4.5,
      src: "bin",
      s: {
        mr: 2
      },
      color: "danger"
    }), t("common.actions.delete")));
  })), /*#__PURE__*/React.createElement(Table.Td, null))), hasAfter && /*#__PURE__*/React.createElement(Table.LoadMore, {
    colSpan: columnCount,
    onClick: handleLoadMore,
    isLoading: status === StatusStateEnum.LOADING
  })) : /*#__PURE__*/React.createElement(Table.Tr, null, /*#__PURE__*/React.createElement(Table.Td, {
    colSpan: columnCount,
    noHover: true,
    center: true
  }, /*#__PURE__*/React.createElement(Text, {
    color: "textSecondary",
    fontSize: 5,
    align: "center",
    display: "block",
    s: {
      my: 5
    }
  }, t("RoleManagement.noRolesFound")))))), userHas(AuthoritiesEnum.MANAGE_ROLES_WRITE) && /*#__PURE__*/React.createElement(Modal, {
    open: action === CRUDActionEnum.CREATE
  }, /*#__PURE__*/React.createElement(RoleCreateModal, null)), userHas(AuthoritiesEnum.MANAGE_ROLES_WRITE) && /*#__PURE__*/React.createElement(Modal, {
    open: action === CRUDActionEnum.UPDATE
  }, /*#__PURE__*/React.createElement(RoleUpdateModal, null)), userHas(AuthoritiesEnum.MANAGE_ROLES_WRITE) && /*#__PURE__*/React.createElement(Modal, {
    open: action === CRUDActionEnum.DELETE
  }, /*#__PURE__*/React.createElement(RoleDeleteModal, null)));
};
export default /*#__PURE__*/React.memo(secured({
  permissions: AuthoritiesEnum.MANAGE_ROLES_READ,
  noAccess: () => /*#__PURE__*/React.createElement(RestrictedSection, null)
})(RoleManagement), () => true);